import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ASSETS } from "@/lib/assets";

// Fake mama cards to show a "swipe between mamas" loop
const MAMAS = [
  {
    name: "Camille",
    age: 30,
    city: "Paris",
    km: "0 km",
    kids: "2 enfants",
    bio: "Passionnée de yoga, recherche des amies pour sorties parc et cafés.",
    tags: ["Yoga", "Café", "Parc"],
    img: "https://images.pexels.com/photos/18689091/pexels-photo-18689091.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    name: "Léa",
    age: 28,
    city: "Lyon",
    km: "1,2 km",
    kids: "1 enfant · 8 mois",
    bio: "Nouvelle maman, en post-partum. Envie de balades et de vraies conversations.",
    tags: ["Balade", "Post-partum"],
    img: "https://images.pexels.com/photos/10935456/pexels-photo-10935456.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    name: "Amina",
    age: 33,
    city: "Bordeaux",
    km: "0,4 km",
    kids: "3 ans",
    bio: "Cherche des mamans du quartier pour brunchs et ateliers créatifs.",
    tags: ["Brunch", "Atelier"],
    img: "https://images.pexels.com/photos/27176616/pexels-photo-27176616.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

const EASE = [0.2, 0.7, 0.2, 1];

export default function SwipeLoop() {
  const [idx, setIdx] = useState(0);
  const [action, setAction] = useState(null); // "like" | "pass" | null

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;

    let mounted = true;
    const cycle = async () => {
      while (mounted) {
        await new Promise((r) => setTimeout(r, 2400));
        if (!mounted) return;
        setAction(Math.random() > 0.35 ? "like" : "pass");
        await new Promise((r) => setTimeout(r, 750));
        if (!mounted) return;
        setIdx((v) => (v + 1) % MAMAS.length);
        setAction(null);
      }
    };
    cycle();
    return () => {
      mounted = false;
    };
  }, []);

  const mama = MAMAS[idx];
  const exitX = action === "like" ? 420 : action === "pass" ? -420 : 0;
  const exitRot = action === "like" ? 18 : action === "pass" ? -18 : 0;

  return (
    <div className="relative w-full max-w-[320px] mx-auto">
      <div className="mm-iphone">
        <div className="mm-iphone-screen bg-[#FBF6EF]">
          {/* Fake app chrome */}
          <div className="absolute inset-x-0 top-0 pt-11 pb-2 px-5 flex items-center justify-between z-10">
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#D97757]" />
              <span className="text-[13px] font-semibold text-[#D97757]">
                MamaMatch
              </span>
            </div>
            <div className="flex gap-1.5">
              <span className="w-5 h-5 rounded-full bg-[#0A0A0A]/8" />
              <span className="w-5 h-5 rounded-full bg-[#0A0A0A]/8" />
            </div>
          </div>

          {/* Card stack */}
          <div className="absolute inset-x-4 top-[76px] bottom-[110px]">
            {/* backdrop next card */}
            <div className="absolute inset-0 rounded-2xl bg-[#0A0A0A]/5 translate-y-2 scale-[0.96]" />

            <AnimatePresence mode="wait">
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30, scale: 0.98 }}
                animate={{
                  opacity: action ? 1 : 1,
                  x: action ? exitX : 0,
                  rotate: action ? exitRot : 0,
                  scale: 1,
                  y: 0,
                }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.7, ease: EASE }}
                className="absolute inset-0 rounded-2xl overflow-hidden shadow-[0_12px_30px_-15px_rgba(10,10,10,0.4)]"
              >
                <img
                  src={mama.img}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover"
                  draggable={false}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

                {/* LIKE / NOPE stamps */}
                <AnimatePresence>
                  {action === "like" && (
                    <motion.div
                      key="like"
                      initial={{ opacity: 0, scale: 0.7, rotate: -20 }}
                      animate={{ opacity: 1, scale: 1, rotate: -12 }}
                      exit={{ opacity: 0 }}
                      className="absolute top-4 left-4 px-3 py-1 rounded-md border-2 border-[#D97757] text-[#D97757] font-bold text-sm tracking-wider"
                    >
                      MATCH
                    </motion.div>
                  )}
                  {action === "pass" && (
                    <motion.div
                      key="pass"
                      initial={{ opacity: 0, scale: 0.7, rotate: 20 }}
                      animate={{ opacity: 1, scale: 1, rotate: 12 }}
                      exit={{ opacity: 0 }}
                      className="absolute top-4 right-4 px-3 py-1 rounded-md border-2 border-white text-white font-bold text-sm tracking-wider"
                    >
                      PASSER
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="absolute inset-x-0 bottom-0 p-3.5 text-white">
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-[19px] font-bold">
                      {mama.name}, {mama.age}
                    </span>
                    <span className="w-4 h-4 rounded-full bg-[#4FBF6A] flex items-center justify-center text-[10px]">
                      ✓
                    </span>
                  </div>
                  <div className="mt-0.5 text-[11.5px] text-white/90">
                    📍 {mama.city} · {mama.km}
                  </div>
                  <div className="mt-0.5 text-[11.5px] text-white/90">
                    👶 {mama.kids}
                  </div>
                  <p className="mt-1.5 text-[10.5px] leading-snug text-white/85 line-clamp-2">
                    {mama.bio}
                  </p>
                  <div className="mt-2 flex gap-1 flex-wrap">
                    {mama.tags.map((t) => (
                      <span
                        key={t}
                        className="text-[10px] bg-white/25 backdrop-blur-sm rounded-full px-2 py-0.5"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Action buttons */}
          <div className="absolute inset-x-0 bottom-[54px] flex justify-center gap-4">
            <motion.div
              animate={{
                scale: action === "pass" ? 1.15 : 1,
                backgroundColor:
                  action === "pass" ? "#0A0A0A" : "rgba(255,255,255,1)",
              }}
              transition={{ duration: 0.3 }}
              className="w-10 h-10 rounded-full flex items-center justify-center shadow-[0_4px_12px_rgba(10,10,10,0.15)]"
            >
              <span
                className={`text-[15px] ${
                  action === "pass" ? "text-white" : "text-[#D97757]"
                }`}
              >
                ✕
              </span>
            </motion.div>
            <motion.div
              animate={{
                scale: action === "like" ? 1.15 : 1,
                backgroundColor:
                  action === "like" ? "#D97757" : "rgba(255,255,255,1)",
              }}
              transition={{ duration: 0.3 }}
              className="w-10 h-10 rounded-full flex items-center justify-center shadow-[0_4px_12px_rgba(10,10,10,0.15)]"
            >
              <span
                className={`text-[15px] ${
                  action === "like" ? "text-white" : "text-[#D97757]"
                }`}
              >
                ♥
              </span>
            </motion.div>
          </div>

          {/* Tab bar */}
          <div className="absolute inset-x-0 bottom-0 h-[46px] bg-white/95 backdrop-blur border-t border-[#0A0A0A]/6 flex items-center justify-around text-[9.5px] text-[#6B6660]">
            <span className="text-[#D97757] font-semibold">Découvrir</span>
            <span>Événements</span>
            <span>Chat</span>
            <span>Matchs</span>
            <span>Profil</span>
          </div>
        </div>
      </div>
    </div>
  );
}
